import { SocialAccount, AuthUrlResponse, ProjectData, ShareFormData, SocialPlatform } from '../components/SocialMediaShare/types';
import { API_BASE_URL } from '../config/api';

class SocialMediaService {
  // Get user's connected social media accounts
  async getConnectedAccounts(): Promise<SocialAccount[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/social-media/accounts`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch connected accounts');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching connected accounts:', error);
      throw error;
    }
  }

  // Get OAuth URL for platform authentication
  async getAuthUrl(platform: SocialPlatform): Promise<AuthUrlResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/social-media/auth/${platform}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`Failed to get ${platform} auth URL`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error getting ${platform} auth URL:`, error);
      throw error;
    }
  }
  // Get project data with title/description info
  async getProjectData(projectId: string): Promise<ProjectData> {
    try {
      const response = await fetch(`${API_BASE_URL}/social-media/project-data/${projectId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch project data');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching project data:', error);
      throw error;
    }
  }

  // Create a social media post
  async createSocialPost(shareData: ShareFormData): Promise<any> {
    try {
      const response = await fetch(`${API_BASE_URL}/social-media/posts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(shareData),
      });
      
      if (!response.ok) {
        throw new Error('Failed to create social media post');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error creating social media post:', error);
      throw error;
    }
  }

  // Get user's published posts
  async getUserPosts(): Promise<any[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/social-media/posts`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch user posts');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching user posts:', error);
      throw error;
    }
  }

  // Open OAuth popup window with message-based communication
  openAuthPopup(authUrl: string, platform: SocialPlatform): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const popup = window.open(
        authUrl,
        `${platform}-auth`,
        'width=600,height=700,scrollbars=yes,resizable=yes'
      );

      if (!popup) {
        reject(new Error('Failed to open popup window. Please allow popups for this site.'));
        return;
      }

      let isResolved = false;

      // Listen for OAuth messages from popup
      const handleMessage = (event: MessageEvent) => {
        // Only accept messages from our own domain for security
        if (event.origin !== window.location.origin) return;
        
        const { type, platform: messagePlatform, message, accountId } = event.data;
        
        // Only handle messages for this specific platform
        if (messagePlatform !== platform) return;
        
        if (type === 'OAUTH_SUCCESS') {
          cleanup();
          if (!isResolved) {
            isResolved = true;
            resolve(true);
          }
        } else if (type === 'OAUTH_ERROR') {
          cleanup();
          if (!isResolved) {
            isResolved = true;
            const errorMessage = message ? decodeURIComponent(message) : 'OAuth authentication failed';
            reject(new Error(errorMessage));
          }
        } else if (type === 'OAUTH_CANCELLED') {
          cleanup();
          if (!isResolved) {
            isResolved = true;
            reject(new Error('Authentication was cancelled by user'));
          }
        }
      };

      // Cleanup function
      const cleanup = () => {
        window.removeEventListener('message', handleMessage);
        clearTimeout(timeout);
        
        // Try to close popup if still open (may fail due to COOP, but that's okay)
        try {
          if (popup && !popup.closed) {
            popup.close();
          }
        } catch (e) {
          // Ignore COOP errors when trying to close popup
          console.log('Could not check/close popup (COOP restriction)');
        }
      };

      // Add message listener
      window.addEventListener('message', handleMessage);

      // Timeout (2 minutes)
      const timeout = setTimeout(() => {
        cleanup();
        if (!isResolved) {
          isResolved = true;
          reject(new Error('Authentication timeout - please try again'));
        }
      }, 120000); // 2 minutes
    });
  }
}

export const socialMediaService = new SocialMediaService();
